// Parcel Shipping Settings — Phase 1A / Checkpoint 1 (Feb 2026).
//
// Tenant admin page for configuring parcel shipping (USPS + FedEx):
//   * Tenant defaults: enable, allowed carriers, free-shipping threshold.
//   * Package presets library (Small Box, Medium Box, …).
//   * Per-catalog override (narrows tenant carrier list; cannot extend).
//   * Readiness inspector — Configuration is hydrated; Validation and
//     Operational sections show "Coming in Phase 1B" placeholders per
//     the frozen implementation plan (Correction to Decision 3).
//
// No customer flow. No rate call. No label. That work arrives in
// Checkpoints 2 → 5.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import {
  Package, Truck, Boxes, ClipboardCheck, Trash2, Plus, Save, Loader2, CheckCircle2, XCircle, Info,
} from "lucide-react";
import { toast } from "sonner";

const SUPPORTED_CARRIERS = ["usps", "fedex"];
const WEIGHT_UNITS = ["oz", "lb", "g", "kg"];
const DIM_UNITS = ["in", "cm"];

const TABS = [
  { key: "config",    label: "Tenant Defaults", icon: Truck },
  { key: "presets",   label: "Package Presets", icon: Boxes },
  { key: "catalogs",  label: "Catalog Overrides", icon: Package },
  { key: "readiness", label: "Readiness", icon: ClipboardCheck },
];

export default function ParcelShippingSettingsPage() {
  const [tab, setTab] = useState("config");
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [presets, setPresets] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [readiness, setReadiness] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [cfgRes, presetsRes, catRes, readyRes] = await Promise.all([
        api.get("/commerce/parcel-shipping/config"),
        api.get("/commerce/parcel-shipping/presets"),
        api.get("/retail/catalogs"),
        api.get("/commerce/parcel-shipping/readiness"),
      ]);
      setConfig(cfgRes.data);
      setPresets(presetsRes.data || []);
      setCatalogs(catRes.data?.catalogs || []);
      setReadiness(readyRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load parcel shipping settings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="parcel-shipping-settings-page">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold" data-testid="parcel-shipping-title">
            Parcel Shipping
          </h1>
          <p className="text-sm text-muted-foreground">
            USPS + FedEx configuration for the customer parcel-shipping workflow.
            Configure tenant defaults, package presets, and catalog overrides here — customer checkout, live rate
            quotes, and address validation are already live once the Readiness checklist below is green.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 border-b" data-testid="parcel-shipping-tabs">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`parcel-shipping-tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition
                  ${active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}
                `}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {loading || !config ? (
          <div className="flex items-center gap-2 py-8 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading...
          </div>
        ) : (
          <>
            {tab === "config"    && <ConfigTab    config={config} onSaved={load} />}
            {tab === "presets"   && <PresetsTab   presets={presets} onChanged={load} />}
            {tab === "catalogs"  && <CatalogsTab  catalogs={catalogs} tenantCarriers={config.allowed_carriers} onChanged={load} presets={presets} />}
            {tab === "readiness" && <ReadinessTab readiness={readiness} onRefresh={load} onNavigateTab={setTab} />}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

// ---------------------------------------------------------------------------
// Config tab — tenant defaults
// ---------------------------------------------------------------------------

function ConfigTab({ config, onSaved }) {
  const [enabled, setEnabled] = useState(!!config.parcel_shipping_enabled);
  const [carriers, setCarriers] = useState(config.allowed_carriers || []);
  const [threshold, setThreshold] = useState(config.free_parcel_threshold ?? 0);
  const [defaultPreset, setDefaultPreset] = useState(config.default_preset_id || "");
  const [saving, setSaving] = useState(false);

  const toggleCarrier = (c) => setCarriers(prev =>
    prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]
  );

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        parcel_shipping_enabled: enabled,
        allowed_carriers: carriers,
        free_parcel_threshold: Number(threshold) || 0,
        default_preset_id: defaultPreset || null,
      };
      const { data } = await api.patch("/commerce/parcel-shipping/config", patch);
      toast.success("Parcel shipping settings saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Card className="p-6 space-y-6" data-testid="parcel-shipping-config-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-lg font-semibold">Enable Parcel Shipping</div>
          <div className="text-sm text-muted-foreground">
            Master switch for this tenant. Individual catalogs may still opt out.
          </div>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="parcel-shipping-enabled-switch" />
      </div>

      <div>
        <Label className="block mb-2">Allowed Carriers</Label>
        <div className="flex flex-wrap gap-2">
          {SUPPORTED_CARRIERS.map(c => {
            const on = carriers.includes(c);
            return (
              <button
                key={c}
                onClick={() => toggleCarrier(c)}
                data-testid={`parcel-shipping-carrier-toggle-${c}`}
                className={`px-3 py-1.5 rounded-full text-sm border transition
                  ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background text-foreground border-muted"}`}
              >
                {c.toUpperCase()}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Carriers here must already be credentialed in the Shipping Admin.
          Only USPS and FedEx are supported in Phase 1A.
        </p>
      </div>

      <div>
        <Label className="block mb-2">Free Shipping Threshold (USD)</Label>
        <Input
          type="number"
          min="0"
          step="0.01"
          value={threshold}
          onChange={e => setThreshold(e.target.value)}
          className="w-40"
          data-testid="parcel-shipping-threshold-input"
        />
        <p className="text-xs text-muted-foreground mt-2">
          When the qualifying merchandise subtotal reaches this amount, customer shipping charge becomes zero.
          The carrier rate + tenant subsidy are still recorded for accounting. Set to 0 to disable free shipping.
        </p>
      </div>

      <div>
        <Label className="block mb-2">Default Package Preset (optional)</Label>
        <select
          className="border rounded px-3 py-2 bg-background"
          value={defaultPreset}
          onChange={e => setDefaultPreset(e.target.value)}
          data-testid="parcel-shipping-default-preset-select"
        >
          <option value="">— none —</option>
          {/* fed from parent through onSaved refresh; okay to be empty until presets tab is used */}
        </select>
      </div>

      <div>
        <Button onClick={save} disabled={saving} data-testid="parcel-shipping-save-config-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Tenant Defaults
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Presets tab
// ---------------------------------------------------------------------------

const EMPTY_PRESET = {
  name: "", length: "", width: "", height: "", dim_unit: "in",
  tare_weight: "", max_packed_weight: "", weight_unit: "lb", is_default: false,
};

function PresetsTab({ presets, onChanged }) {
  const [editing, setEditing] = useState(null); // { mode: 'create' | 'edit', preset }
  const openCreate = () => setEditing({ mode: "create", preset: { ...EMPTY_PRESET } });
  const openEdit = (p) => setEditing({ mode: "edit", preset: { ...p } });
  const close = () => setEditing(null);

  const remove = async (p) => {
    if (!confirm(`Delete preset "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/commerce/parcel-shipping/presets/${p.id}`);
      toast.success("Preset deleted");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Card className="p-6 space-y-4" data-testid="parcel-shipping-presets-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-lg font-semibold">Package Presets</div>
          <div className="text-sm text-muted-foreground">
            Staff selects the smallest fitting preset when packing an order. The customer-facing quote uses this dimension set to
            request USPS + FedEx rates. Weight is the maximum the preset should carry.
          </div>
        </div>
        <Button onClick={openCreate} data-testid="parcel-shipping-add-preset-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Preset
        </Button>
      </div>

      {presets.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6">
          No presets yet. Add your first preset (e.g. Small Box · 10×8×4in · max 3 lb).
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Dimensions</TableHead>
              <TableHead>Max Packed Weight</TableHead>
              <TableHead>Tare</TableHead>
              <TableHead>Default</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {presets.map(p => (
              <TableRow key={p.id} data-testid={`parcel-shipping-preset-row-${p.name}`}>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell>{p.length} × {p.width} × {p.height} {p.dim_unit}</TableCell>
                <TableCell>{p.max_packed_weight} {p.weight_unit}</TableCell>
                <TableCell>{p.tare_weight || 0} {p.weight_unit}</TableCell>
                <TableCell>{p.is_default ? <Badge>Default</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`parcel-shipping-preset-edit-${p.name}`}>Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => remove(p)} data-testid={`parcel-shipping-preset-delete-${p.name}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <PresetEditor editing={editing} onClose={close} onSaved={onChanged} />
    </Card>
  );
}

function PresetEditor({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_PRESET);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (editing) setForm({ ...EMPTY_PRESET, ...editing.preset });
  }, [editing]);

  if (!editing) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name?.trim(),
        length: Number(form.length),
        width: Number(form.width),
        height: Number(form.height),
        dim_unit: form.dim_unit,
        tare_weight: Number(form.tare_weight) || 0,
        max_packed_weight: Number(form.max_packed_weight),
        weight_unit: form.weight_unit,
        is_default: !!form.is_default,
      };
      if (editing.mode === "create") {
        await api.post("/commerce/parcel-shipping/presets", payload);
      } else {
        await api.patch(`/commerce/parcel-shipping/presets/${editing.preset.id}`, payload);
      }
      toast.success(editing.mode === "create" ? "Preset created" : "Preset updated");
      onClose();
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="parcel-shipping-preset-editor-dialog">
        <DialogHeader>
          <DialogTitle>{editing.mode === "create" ? "Add Package Preset" : `Edit ${editing.preset.name}`}</DialogTitle>
          <DialogDescription>Dimensions and weight define what fits and what carrier services can be quoted.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Name</Label>
            <Input value={form.name} onChange={e => set("name", e.target.value)} data-testid="preset-name-input" />
          </div>

          <div>
            <Label>Length</Label>
            <Input type="number" min="0" step="0.1" value={form.length} onChange={e => set("length", e.target.value)} data-testid="preset-length-input" />
          </div>
          <div>
            <Label>Width</Label>
            <Input type="number" min="0" step="0.1" value={form.width} onChange={e => set("width", e.target.value)} data-testid="preset-width-input" />
          </div>
          <div>
            <Label>Height</Label>
            <Input type="number" min="0" step="0.1" value={form.height} onChange={e => set("height", e.target.value)} data-testid="preset-height-input" />
          </div>
          <div>
            <Label>Dim Unit</Label>
            <select
              className="border rounded px-3 py-2 w-full bg-background"
              value={form.dim_unit}
              onChange={e => set("dim_unit", e.target.value)}
              data-testid="preset-dim-unit-select"
            >
              {DIM_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>

          <div>
            <Label>Max Packed Weight</Label>
            <Input type="number" min="0" step="0.01" value={form.max_packed_weight} onChange={e => set("max_packed_weight", e.target.value)} data-testid="preset-max-weight-input" />
          </div>
          <div>
            <Label>Weight Unit</Label>
            <select
              className="border rounded px-3 py-2 w-full bg-background"
              value={form.weight_unit}
              onChange={e => set("weight_unit", e.target.value)}
              data-testid="preset-weight-unit-select"
            >
              {WEIGHT_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>

          <div className="col-span-2">
            <Label>Tare Weight (empty box weight, optional)</Label>
            <Input type="number" min="0" step="0.01" value={form.tare_weight} onChange={e => set("tare_weight", e.target.value)} data-testid="preset-tare-input" />
          </div>

          <div className="col-span-2 flex items-center gap-2">
            <Checkbox
              id="is-default"
              checked={!!form.is_default}
              onCheckedChange={(v) => set("is_default", !!v)}
              data-testid="preset-default-checkbox"
            />
            <Label htmlFor="is-default" className="cursor-pointer">
              Set as default preset (used when nothing else fits the cart heuristic)
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="parcel-shipping-preset-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Catalog overrides tab
// ---------------------------------------------------------------------------

function CatalogsTab({ catalogs, tenantCarriers, onChanged, presets }) {
  return (
    <Card className="p-6 space-y-4" data-testid="parcel-shipping-catalogs-card">
      <div>
        <div className="text-lg font-semibold">Per-Catalog Overrides</div>
        <div className="text-sm text-muted-foreground">
          Each catalog can narrow the tenant carrier list, override the free-shipping threshold, or disable parcel shipping entirely.
          Overrides <b>never</b> add carriers the tenant has not credentialed.
        </div>
      </div>

      {catalogs.length === 0 ? (
        <div className="text-sm text-muted-foreground py-4">No catalogs yet.</div>
      ) : (
        <div className="space-y-4">
          {catalogs.map(cat => (
            <CatalogOverrideRow
              key={cat.id}
              catalog={cat}
              tenantCarriers={tenantCarriers || []}
              presets={presets}
              onSaved={onChanged}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function CatalogOverrideRow({ catalog, tenantCarriers, presets, onSaved }) {
  const ov = catalog.parcel_shipping || {};
  const [enabled, setEnabled] = useState(ov.parcel_shipping_enabled ?? null);
  const [carriers, setCarriers] = useState(ov.allowed_carriers ?? null);
  const [threshold, setThreshold] = useState(ov.free_parcel_threshold ?? "");
  const [defaultPreset, setDefaultPreset] = useState(ov.default_preset_id || "");
  const [saving, setSaving] = useState(false);

  const toggleCarrier = (c) => {
    setCarriers(prev => {
      const cur = prev ?? [];
      return cur.includes(c) ? cur.filter(x => x !== c) : [...cur, c];
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      if (enabled !== null) patch.parcel_shipping_enabled = !!enabled;
      if (carriers !== null) patch.allowed_carriers = carriers;
      if (threshold !== "" && threshold !== null) patch.free_parcel_threshold = Number(threshold);
      if (defaultPreset) patch.default_preset_id = defaultPreset;
      await api.patch(`/commerce/parcel-shipping/catalogs/${catalog.id}`, patch);
      toast.success(`Override saved for ${catalog.name}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="border rounded p-4 space-y-3" data-testid={`parcel-shipping-catalog-row-${catalog.id}`}>
      <div className="flex items-center justify-between">
        <div className="font-medium">{catalog.name}</div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Override enabled?</span>
          <Switch
            checked={enabled === true}
            onCheckedChange={(v) => setEnabled(v)}
            data-testid={`parcel-shipping-catalog-enabled-${catalog.id}`}
          />
        </div>
      </div>

      <div>
        <Label className="block mb-1 text-xs">Allowed Carriers (must be subset of tenant list)</Label>
        <div className="flex gap-2 flex-wrap">
          {tenantCarriers.length === 0 ? (
            <span className="text-xs text-muted-foreground">Tenant has no carriers configured yet.</span>
          ) : tenantCarriers.map(c => {
            const on = (carriers ?? []).includes(c);
            return (
              <button
                key={c}
                onClick={() => toggleCarrier(c)}
                data-testid={`parcel-shipping-catalog-carrier-${catalog.id}-${c}`}
                className={`px-3 py-1 rounded-full text-xs border ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background border-muted"}`}
              >
                {c.toUpperCase()}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex gap-4 flex-wrap items-end">
        <div>
          <Label className="block mb-1 text-xs">Free Shipping Threshold (override, blank = inherit)</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={threshold}
            onChange={e => setThreshold(e.target.value)}
            className="w-40"
            data-testid={`parcel-shipping-catalog-threshold-${catalog.id}`}
          />
        </div>
        <div>
          <Label className="block mb-1 text-xs">Default Preset (override, blank = inherit)</Label>
          <select
            className="border rounded px-3 py-2 bg-background"
            value={defaultPreset}
            onChange={e => setDefaultPreset(e.target.value)}
            data-testid={`parcel-shipping-catalog-preset-${catalog.id}`}
          >
            <option value="">— inherit —</option>
            {presets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <Button onClick={save} disabled={saving} data-testid={`parcel-shipping-catalog-save-${catalog.id}`}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Override
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Readiness tab (three categories; Validation + Operational placeholder in Phase 1A)
// ---------------------------------------------------------------------------

// Maps each readiness item's key to operator-facing corrective guidance.
// This is the PRIMARY text shown when a card is red — the raw backend
// `reason` code (e.g. "no_catalog_opted_in") is kept only as a secondary
// diagnostic detail, never as the main instruction (Phase 1, USPS
// Production Enablement — operators reported the codes alone weren't
// actionable). `tab` navigates within this page; `route` navigates to a
// different admin page. Exported so tests can verify the mapping directly
// without mounting the component.
export const READINESS_GUIDANCE = {
  parcel_shipping_enabled: {
    action: "Go to Parcel Shipping → Tenant Defaults, turn on Enable Parcel Shipping, select at least one configured carrier, and save.",
    tab: "config",
  },
  usps_configured: {
    action: "Go to Shipping → Settings, configure and enable the carrier in Sandbox or Live mode, then validate credentials.",
    route: "/dashboard/shipping",
  },
  fedex_configured: {
    action: "Go to Shipping → Settings, configure and enable the carrier in Sandbox or Live mode, then validate credentials.",
    route: "/dashboard/shipping",
  },
  origin_warehouse: {
    action: "Go to Shipping → Settings → Warehouses, add a complete origin address, and mark one warehouse as default.",
    route: "/dashboard/shipping",
  },
  package_presets: {
    action: "Go to Parcel Shipping → Package Presets and create at least one complete package preset.",
    tab: "presets",
  },
  shippable_products: {
    action: "Edit at least one product in a Parcel Shipping catalog, enable “Shippable via Parcel Shipping,” and provide weight plus length, width, and height.",
  },
  catalog_parcel_enabled: {
    action: "Go to Parcel Shipping → Catalog Overrides, enable the override for a published catalog that has Parcel Shipping selected under Fulfillment Methods, select an allowed carrier, and save.",
    tab: "catalogs",
  },
  free_shipping_policy: {
    action: "Optional. Go to Parcel Shipping → Tenant Defaults and set a free-shipping threshold if you want one.",
    tab: "config",
  },
};

export function readinessGuidanceFor(itemKey) {
  return READINESS_GUIDANCE[itemKey] || null;
}

function ReadinessTab({ readiness, onRefresh, onNavigateTab }) {
  const navigate = useNavigate();
  if (!readiness) return null;
  const c = readiness.configuration || {};

  const ITEMS = [
    { key: "parcel_shipping_enabled", label: "Parcel Shipping Enabled" },
    { key: "usps_configured",         label: "USPS Configured" },
    { key: "fedex_configured",        label: "FedEx Configured" },
    { key: "origin_warehouse",        label: "Origin Warehouse Configured" },
    { key: "package_presets",         label: "Package Presets Configured" },
    { key: "shippable_products",      label: "Shippable Products Exist" },
    { key: "catalog_parcel_enabled",  label: "At Least One Catalog Opted In" },
    { key: "free_shipping_policy",    label: "Free Shipping Policy (optional)" },
  ];

  const goTo = (guidance) => {
    if (!guidance) return;
    if (guidance.route) navigate(guidance.route);
    else if (guidance.tab) onNavigateTab?.(guidance.tab);
  };

  return (
    <div className="space-y-4">
      <Card className="p-6 space-y-4" data-testid="parcel-shipping-readiness-config-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-lg font-semibold">Configuration Readiness</div>
            <div className="text-sm text-muted-foreground">
              Prerequisite settings customers' Parcel Shipping checkout depends on. Green = ready. Red = see the guidance below each item.
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={onRefresh} data-testid="parcel-shipping-readiness-refresh">
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ITEMS.map(item => {
            const v = c[item.key] || { ok: false };
            const guidance = !v.ok ? readinessGuidanceFor(item.key) : null;
            return (
              <div key={item.key} className="border rounded p-3 flex items-start gap-3" data-testid={`readiness-config-${item.key}`}>
                {v.ok ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <div className="font-medium text-sm">{item.label}</div>
                  {v.ok ? (
                    <div className="text-xs text-green-700 mt-0.5">Ready</div>
                  ) : guidance ? (
                    <div className="text-xs text-slate-700 mt-0.5" data-testid={`readiness-guidance-${item.key}`}>
                      {guidance.action}
                    </div>
                  ) : v.reason ? (
                    <div className="text-xs text-muted-foreground mt-0.5">{v.reason}</div>
                  ) : null}
                  {typeof v.count === "number" ? (
                    <div className="text-xs text-muted-foreground mt-0.5">Count: {v.count}</div>
                  ) : null}
                  {typeof v.threshold === "number" ? (
                    <div className="text-xs text-muted-foreground mt-0.5">Threshold: ${v.threshold.toFixed(2)}</div>
                  ) : null}
                  {!v.ok && guidance && (guidance.tab || guidance.route) ? (
                    <Button
                      variant="link" size="sm" className="h-auto p-0 mt-1 text-xs"
                      onClick={() => goTo(guidance)}
                      data-testid={`readiness-fix-${item.key}`}
                    >
                      Fix this →
                    </Button>
                  ) : null}
                  {!v.ok && v.reason ? (
                    <div className="text-[10px] text-muted-foreground/70 mt-1 font-mono" data-testid={`readiness-reason-code-${item.key}`}>
                      code: {v.reason}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="p-6 opacity-70" data-testid="parcel-shipping-readiness-validation-card">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-500 mt-1" />
          <div>
            <div className="text-lg font-semibold">Validation Readiness — Coming in Phase 1B</div>
            <div className="text-sm text-muted-foreground mb-2">
              This section will hydrate once the platform records last-preflight and last credential-validation timestamps against a real carrier sandbox tenant (CP0-B) and later against live traffic (CP6).
            </div>
            <ul className="text-sm text-muted-foreground list-disc pl-5">
              <li>Last Preflight — <b>not yet run</b></li>
              <li>Last USPS Credential Validation — <b>not yet run</b></li>
              <li>Last FedEx Credential Validation — <b>not yet run</b></li>
            </ul>
          </div>
        </div>
      </Card>

      <Card className="p-6 opacity-70" data-testid="parcel-shipping-readiness-operational-card">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-500 mt-1" />
          <div>
            <div className="text-lg font-semibold">Operational Readiness — Coming in Phase 1B</div>
            <div className="text-sm text-muted-foreground mb-2">
              This section will hydrate once labels are being purchased against live carriers.
            </div>
            <ul className="text-sm text-muted-foreground list-disc pl-5">
              <li>Last Successful Label</li>
              <li>Last Label Purchase Attempt</li>
              <li>Last Carrier Error</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
}
